import { useEffect, useMemo, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { Button } from "@/components/ui/Button";
import { Checkbox, TextInput } from "@/components/ui/Field";
import { Autocomplete } from "@/components/Autocomplete";
import {
  createDeliveryAddress,
  deleteDeliveryAddress,
  placesAutocomplete,
  placesDetails,
  placesHealth,
  updateDeliveryAddress,
  type DeliveryAddress,
} from "@/lib/orders";

export function DeliveryAddressDialog({
  open,
  mode,
  initial,
  onClose,
  onSaved,
}: {
  open: boolean;
  mode: "new" | "edit";
  initial: DeliveryAddress | null;
  onClose: () => void;
  onSaved: (saved?: DeliveryAddress) => void;
}) {
  const [abk, setAbk] = useState("");
  const [firma, setFirma] = useState("");
  const [strasse, setStrasse] = useState("");
  const [hausnr, setHausnr] = useState("");
  const [plz, setPlz] = useState("");
  const [stadt, setStadt] = useState("");
  const [placeId, setPlaceId] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [placesOk, setPlacesOk] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const baseline = useMemo(
    () => ({
      abk: initial?.abkuerzung || "",
      firma: initial?.firma || "",
      strasse: initial?.strasse || "",
      hausnr: initial?.hausnummer || "",
      plz: initial?.postleitzahl || "",
      stadt: initial?.stadt || "",
      placeId: initial?.google_place_id || "",
      isDefault: !!initial?.is_default,
    }),
    [initial],
  );

  const dirty = useMemo(
    () =>
      hasFormChanges(
        { abk, firma, strasse, hausnr, plz, stadt, placeId, isDefault },
        baseline,
      ),
    [abk, firma, strasse, hausnr, plz, stadt, placeId, isDefault, baseline],
  );

  useEffect(() => {
    if (!open) return;
    setAbk(initial?.abkuerzung || "");
    setFirma(initial?.firma || "");
    setStrasse(initial?.strasse || "");
    setHausnr(initial?.hausnummer || "");
    setPlz(initial?.postleitzahl || "");
    setStadt(initial?.stadt || "");
    setPlaceId(initial?.google_place_id || "");
    setIsDefault(!!initial?.is_default);
    setError(null);
    void placesHealth().then((h) => setPlacesOk(!!h?.available));
  }, [open, initial]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const payload = {
      abkuerzung: abk.trim() || null,
      firma: firma.trim(),
      strasse: strasse.trim() || null,
      hausnummer: hausnr.trim() || null,
      postleitzahl: plz.trim() || null,
      stadt: stadt.trim() || null,
      google_place_id: placeId.trim() || null,
      is_default: isDefault,
    };
    try {
      let saved: DeliveryAddress;
      if (mode === "edit" && initial?.id) {
        saved = await updateDeliveryAddress(initial.id, payload);
      } else {
        saved = await createDeliveryAddress(payload);
      }
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!initial?.id || !confirm("Lieferadresse wirklich löschen?")) return;
    setBusy(true);
    try {
      await deleteDeliveryAddress(initial.id);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DeliveryAddressDialogView
      open={open}
      onClose={onClose}
      dirty={dirty}
      mode={mode}
      busy={busy}
      error={error}
      placesOk={placesOk}
      abk={abk}
      setAbk={setAbk}
      firma={firma}
      setFirma={setFirma}
      strasse={strasse}
      setStrasse={setStrasse}
      hausnr={hausnr}
      setHausnr={setHausnr}
      plz={plz}
      setPlz={setPlz}
      stadt={stadt}
      setStadt={setStadt}
      setPlaceId={setPlaceId}
      isDefault={isDefault}
      setIsDefault={setIsDefault}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
    />
  );
}

function DeliveryAddressDialogView({
  open,
  onClose,
  dirty,
  mode,
  busy,
  error,
  placesOk,
  abk,
  setAbk,
  firma,
  setFirma,
  strasse,
  setStrasse,
  hausnr,
  setHausnr,
  plz,
  setPlz,
  stadt,
  setStadt,
  setPlaceId,
  isDefault,
  setIsDefault,
  onSubmit,
  onDelete,
}: {
  open: boolean;
  onClose: () => void;
  dirty: boolean;
  mode: "new" | "edit";
  busy: boolean;
  error: string | null;
  placesOk: boolean;
  abk: string;
  setAbk: (v: string) => void;
  firma: string;
  setFirma: (v: string) => void;
  strasse: string;
  setStrasse: (v: string) => void;
  hausnr: string;
  setHausnr: (v: string) => void;
  plz: string;
  setPlz: (v: string) => void;
  stadt: string;
  setStadt: (v: string) => void;
  setPlaceId: (v: string) => void;
  isDefault: boolean;
  setIsDefault: (v: boolean) => void;
  onSubmit: (e: React.FormEvent) => void;
  onDelete: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={mode === "edit" ? "Lieferadresse bearbeiten" : "Neue Lieferadresse"}
      size="md"
      zIndexClass="z-[130]"
      footer={
        <>
          {mode === "edit" ? (
            <Button type="button" variant="danger" disabled={busy} onClick={() => void onDelete()}>
              Löschen
            </Button>
          ) : null}
          <Button type="button" variant="secondary" onClick={requestClose}>
            Abbrechen
          </Button>
          <Button type="submit" form="delivery-addr-form" loading={busy}>
            Speichern
          </Button>
        </>
      }
    >
      <form id="delivery-addr-form" onSubmit={(e) => void onSubmit(e)} className="space-y-2">
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        <p className="text-xs text-label-tertiary">
          {placesOk
            ? "Tipp: Straße eingeben und Vorschlag wählen – Felder werden automatisch befüllt."
            : "Adress-Suche derzeit nicht verfügbar. Bitte Felder manuell ausfüllen."}
        </p>
        {placesOk ? (
          <Autocomplete
            label="Adresse suchen"
            placeholder="Straße, PLZ, Ort …"
            minChars={2}
            loadOptions={async (q) => {
              const list = await placesAutocomplete(q);
              return list.map((p) => ({
                key: p.place_id,
                label: p.text || [p.primary, p.secondary].filter(Boolean).join(", "),
                value: p.place_id,
              }));
            }}
            onSelect={async (opt) => {
              if (!opt) return;
              const d = await placesDetails(String(opt.value));
              setStrasse(d.strasse || "");
              setHausnr(d.hausnummer || "");
              setPlz(d.postleitzahl || "");
              setStadt(d.stadt || "");
              setPlaceId(d.place_id || String(opt.value));
            }}
          />
        ) : null}
        <TextInput label="Kürzel" value={abk} onChange={(e) => setAbk(e.target.value)} />
        <TextInput label="Firma *" required value={firma} onChange={(e) => setFirma(e.target.value)} />
        <section className="grid gap-2 sm:grid-cols-3">
          <TextInput label="Straße" className="sm:col-span-2" value={strasse} onChange={(e) => setStrasse(e.target.value)} />
          <TextInput label="Hausnr." value={hausnr} onChange={(e) => setHausnr(e.target.value)} />
        </section>
        <section className="grid gap-2 sm:grid-cols-2">
          <TextInput label="PLZ" value={plz} onChange={(e) => setPlz(e.target.value)} />
          <TextInput label="Stadt" value={stadt} onChange={(e) => setStadt(e.target.value)} />
        </section>
        <Checkbox
          label="Als Standard-Lieferadresse verwenden"
          checked={isDefault}
          onChange={(e) => setIsDefault(e.target.checked)}
        />
      </form>
    </Dialog>
  );
}
